import { TFunction } from 'i18next';

import { CreditCardBrand, MaybeNullable, Nullable } from '@types-internal';
import {
  adyenBrandOfCreditCardBrand,
  creditCardBrandOfAdyenBrand,
} from '@types-internal/AdyenTypes';

export const CREDIT_CARD_RENDER_CONTAINER_ID = 'adyen-cc-form-container';
export const BANCONTACT_RENDER_CONTAINER_ID = 'adyen-bcmc-form-container';

export const styles = {
  placeholder: {
    color: '#706E6A',
    fontFamily: 'Nunito',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
  },
  base: {
    // same as the one for .z-input
    appearance: 'none',
    width: '100%',
    height: '100%',
    padding: '15px',
    minWidth: '0',
    color: '#555',
    lineHeight: '1.15',
    fontSize: '14px',
    fontFamily: 'sans-serif',
    fontWeight: '400',
  },
  error: {
    color: '#555',
  },
  validated: {
    color: '#555',
  },
};

export function normalizeAdyenCardBrand(value: unknown): Nullable<CreditCardBrand> {
  if (typeof value === 'string') {
    const creditCardBrand = (
      creditCardBrandOfAdyenBrand as Record<string, MaybeNullable<CreditCardBrand>>
    )[value];
    return creditCardBrand ?? null;
  } else {
    return null;
  }
}

export function getAdyenCardBrands(creditCardBrands: CreditCardBrand[]) {
  const adyenCardBrands = [];

  for (const creditCardBrand of creditCardBrands) {
    const adyenCardBrand = adyenBrandOfCreditCardBrand[creditCardBrand];
    if (adyenCardBrand) {
      adyenCardBrands.push(adyenCardBrand);
    }
  }

  return adyenCardBrands;
}

/**
 * Overrides Adyen's default i18n text and optionally enables placeholders for modernized checkout.
 * If isModernize is false, all placeholders are empty strings (disabling them).
 * See: https://docs.adyen.com/online-payments/web-components/localization-components
 */
export function overrideAdyenTranslations(
  t: TFunction,
  isModernize = false,
): Record<string, string> {
  // Error messages
  const requiredMessage = t('checkout.form.required.error');
  const cardNotSupported = t('checkout.payment.creditcard.type_not_supported');
  const invalidDate = t('checkout.payment.creditcard.expiry_date.invalid');
  const invalidCVC = t('checkout.payment.creditcard.cvc.invalid');
  const invalidYear = t('checkout.payment.creditcard.expiry_year.invalid');
  const sizeError = t('checkout.form.size.error', { '0': '4', '1': '23' });

  const placeholders = isModernize
    ? {
        'creditCard.numberField.placeholder': t(
          'checkout.payment.creditcard.card_number.placeholder',
        ),
        'creditCard.expiryDateField.placeholder': t(
          'checkout.payment.creditcard.holder_name.placeholder',
        ),
        'creditCard.expiryDateField.month.placeholder': t(
          'checkout.payment.creditcard.month.placeholder',
        ),
        'creditCard.expiryDateField.year.placeholder': t(
          'checkout.payment.creditcard.year.placeholder.YY',
        ),
        'creditCard.cvcField.placeholder': t('checkout.payment.creditcard.cvc.placeholder'),
        'creditCard.cvcField.placeholder.4digits': '0000',
        'creditCard.cvcField.placeholder.3digits': t('checkout.payment.creditcard.cvc.placeholder'),
      }
    : {
        'creditCard.numberField.placeholder': '',
        'creditCard.expiryDateField.placeholder': '',
        'creditCard.expiryDateField.month.placeholder': '',
        'creditCard.expiryDateField.year.placeholder': '',
        'creditCard.cvcField.placeholder': '',
        'creditCard.cvcField.placeholder.4digits': '',
        'creditCard.cvcField.placeholder.3digits': '',
      };

  return {
    'error.va.sf-cc-num.01': cardNotSupported,
    'error.va.sf-cc-num.02': requiredMessage,
    'error.va.sf-cc-num.03': cardNotSupported,
    'error.va.sf-cc-num.04': sizeError,
    'error.va.sf-cc-dat.01': invalidYear,
    'error.va.sf-cc-dat.02': invalidYear,
    'error.va.sf-cc-dat.03': invalidDate,
    'error.va.sf-cc-dat.04': requiredMessage,
    'error.va.sf-cc-dat.05': invalidDate,
    'error.va.sf-cc-mth.01': requiredMessage,
    'error.va.sf-cc-yr.01': invalidYear,
    'error.va.sf-cc-yr.02': invalidYear,
    'error.va.sf-cc-cvc.01': invalidCVC,
    'error.va.sf-cc-cvc.02': invalidCVC,
    ...placeholders,
  };
}
